import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiSearch, FiChevronDown } from 'react-icons/fi';

// A native <select> becomes unusable once there are 100+ options (e.g. many
// YouTube destination channels) — this filters an already-fetched, in-memory
// options array by typed text instead of hitting the server, which is plenty
// fast at that scale. Drop-in replacement for a single-select <select>:
// options: { key, label }[]; value/onChange behave like a controlled <select>.
// Keyboard: ↑/↓ to move, Enter to pick, Esc to close.
// searchPlaceholder: the search box's hint (defaults to `placeholder`);
// buttonClassName replaces the trigger's default look (e.g. a compact one).
export const SearchableSelect = ({
  value, onChange, options, placeholder, searchPlaceholder, disabled, className = '', buttonClassName, id, ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [panelStyle, setPanelStyle] = useState(null);
  const listRef = useRef(null);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const queryInputRef = useRef(null);

  const selected = options.find((o) => o.key === value);

  const close = useCallback(() => { setOpen(false); setQuery(''); }, []);

  // The options panel is portaled straight to <body> and positioned via the
  // trigger button's live viewport rect (fixed, not absolute) — computed
  // fresh on every open. That's deliberate: this component gets used inside
  // scroll-clipped containers (table wrappers with overflow-x-auto, this
  // app's own overflow-auto <main>), and a plain absolutely-positioned child
  // panel gets silently clipped/cut off by any of those ancestors. Rendering
  // outside the DOM tree entirely sidesteps that regardless of where this
  // component is used.
  // Opens below the trigger, or above it when there isn't room below (the
  // Media AI and Quick AI pickers sit at the bottom of the screen). At least
  // 260px wide so long prompt names stay readable next to a narrow trigger.
  const place = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(Math.max(rect.width, 260), window.innerWidth - 16);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const below = window.innerHeight - rect.bottom;
    setPanelStyle(below < 280 && rect.top > below
      ? { position: 'fixed', bottom: window.innerHeight - rect.top + 4, left, width, zIndex: 9999 }
      : { position: 'fixed', top: rect.bottom + 4, left, width, zIndex: 9999 });
  }, []);

  const openDropdown = () => {
    place();
    setActive(Math.max(0, options.findIndex((o) => o.key === value)));
    setOpen(true);
  };

  // Focus the search input manually (rather than the `autoFocus` attribute)
  // with `preventScroll: true` — plain `autoFocus` lets the browser scroll
  // the newly-focused element into view, and since the panel can render near
  // the bottom edge of the viewport (the exact scenario this component's
  // fixed-position portal exists for), that auto-scroll would immediately
  // trigger the scroll-closes-the-dropdown listener below, closing the panel
  // the instant it opens.
  useLayoutEffect(() => {
    if (open) queryInputRef.current?.focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      close();
    };
    // The panel is fixed-position, so when the page (or a scroll container)
    // scrolls it follows its trigger, and closes only once the trigger has
    // scrolled out of view. Closing on every scroll closed it the moment it
    // opened on laptop screens: clicking a picker near the bottom edge makes
    // the browser scroll it into view, and that scroll lands just after the
    // click. Scrolling the option list itself is left alone.
    const onScroll = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect || rect.bottom < 0 || rect.top > window.innerHeight) close();
      else place();
    };
    const onResize = () => close();
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close, place]);

  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  // Keep the highlighted option in view as it moves — by scrolling the list
  // itself only. scrollIntoView would also scroll the page whenever the panel
  // reaches past the screen edge.
  useEffect(() => {
    const list = listRef.current;
    const item = list?.children[active];
    if (!open || !item) return;
    if (item.offsetTop < list.scrollTop) list.scrollTop = item.offsetTop;
    else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
  }, [open, active]);

  const pick = (key) => {
    onChange(key);
    close();
    buttonRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[active]) pick(filtered[active].key); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); buttonRef.current?.focus(); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className || 'w-full sm:w-64'}`}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? close() : openDropdown())}
        onKeyDown={(e) => { if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { e.preventDefault(); openDropdown(); } }}
        className={buttonClassName || 'input text-sm w-full flex items-center justify-between gap-2 text-left disabled:opacity-50'}
      >
        <span className={`truncate min-w-0 ${selected ? 'text-gray-800' : 'text-gray-400'}`} title={selected?.label}>
          {selected ? selected.label : (placeholder || '—')}
        </span>
        <FiChevronDown size={13} className="text-gray-400 flex-shrink-0" />
      </button>

      {open && panelStyle && createPortal(
        <div
          ref={panelRef}
          style={panelStyle}
          className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden flex flex-col"
        >
          <div className="flex items-center gap-1.5 px-2.5 py-2 border-b border-gray-100">
            <FiSearch size={12} className="text-gray-400 flex-shrink-0" />
            <input
              ref={queryInputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActive(0); }}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder || placeholder}
              aria-label={searchPlaceholder || placeholder}
              className="text-sm w-full outline-none"
            />
          </div>
          <div ref={listRef} role="listbox" className="relative overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-2.5 text-xs text-gray-400">—</p>
            ) : (
              filtered.map((o, i) => (
                <button
                  type="button"
                  key={o.key}
                  role="option"
                  aria-selected={o.key === value}
                  onClick={() => pick(o.key)}
                  onMouseEnter={() => setActive(i)}
                  className={`w-full text-left px-3 py-2 text-sm break-words ${i === active ? 'bg-gray-100' : ''} ${o.key === value ? 'text-primary-700 font-medium' : 'text-gray-700'}`}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SearchableSelect;
